// import GitInfo from 'react-git-info/macro';
import CryptoJS from 'crypto-js';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import packageJSON from '../../package.json';
import Confirmation from '../components/Confirmation';
import Icon, { Icons } from '../components/Icon/Icon';
import AppConfig from '../config';
import { router } from '../index';
import { LOCALES, t } from '../lang';
import store from '../store';
import { appSlice } from '../store/slices';
import RequestService from './RequestService';
import TypesService from './TypesService';

const { apiUrl, routes } = AppConfig;

const langFormat = {
	ru: 'ru-Ru',
	en: 'en-EN',
	uk: 'uk-UA',
};

const CREDIT_CURRENCY = packageJSON?.creditCurrency ?? 'Credits';

const HelperService = {
	Init: async () => {
		// const res = await fetch(`${process.env.PUBLIC_URL}/config.json`);
		// const config = await res.json();
		const config = window.__APP_CONFIG__;

		store.dispatch(appSlice.actions.updatePublicConfig(config || { apiUrl }));
	},
	FormatFiatWithoutCurrency: (number, roundingMode) => {
		return new Intl.NumberFormat('ru-RU', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
			roundingMode,
		})
			.format(number)
			.replace(',', '.');
	},
	FormatPercent: (number, fractionDigits = 1) => {
		return new Intl.NumberFormat('ru-RU', {
			minimumFractionDigits: fractionDigits,
		}).format(number);
	},
	Sleep: ms => new Promise(res => setTimeout(res, ms)),
	FormatDateMonthTextDayNumber: date => {
		const activeLanguage = langFormat[store.getState().localization.lang];

		return new Intl.DateTimeFormat(activeLanguage, {
			day: 'numeric',
			month: 'long',
			year: 'numeric',
		}).formatToParts(date);
	},

	getBlockchainByAddress: address => {
		if (/^(?:[13][a-km-zA-HJ-NP-Z1-9]{25,34}|(?:bc1|tb1)[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{25,59})$/i.test(address))
			return TypesService.BlockchainsById[1]; // 'BTC'
		if (/T[A-Za-z1-9]{33}/.test(address) && address?.length === 34) return TypesService.BlockchainsById[2]; // 'TRON'
		if (/^0x[a-fA-F0-9]{40}$/.test(address)) return TypesService.BlockchainsById[3]; // 'ETH'
		if (address && /^([LM3]{1}[a-km-zA-HJ-NP-Z1-9]{26,33}||(t?ltc1)[a-z0-9]{39,59})$/.test(address))
			return TypesService.BlockchainsById[5]; // 'LTC'
		// TODO

		// return "Didn't find";
		return null;
	},

	FormatCrypto: (number, fractionDigits, roundingMode) => {
		return new Intl.NumberFormat('ru-RU', {
			minimumFractionDigits: fractionDigits || 8,
			maximumFractionDigits: fractionDigits || 8,
			roundingMode,
		})
			.format(number)
			.replace(',', '.');
	},

	getAmlRiskByText(percent, isActualAML, blockchain, isError, checkDate) {
		const riskLevels =
			store.getState().address.AmlBlockchainsSettings?.[blockchain]?.AmlLevels ||
			store.getState().address.AmlLevelsDefault;

		let risk;

		const riskStatus = {
			checked: isActualAML && percent !== null,
			outdated: !isActualAML && percent !== null,
			unchecked: !checkDate && !isActualAML && percent === null,
			Unverified: !!checkDate && percent === null,
		};

		if (isError) {
			risk = 'ErrorTriangle';
			riskStatus.unchecked = false;
		} else if (riskStatus.checked) {
			risk = percent > riskLevels.Low ? (percent > riskLevels.Medium ? 'High' : 'Medium') : 'Low';
		} else {
			risk = riskStatus.Unverified ? 'Unverified' : riskStatus.outdated ? 'Outdated' : 'Unchecked';
		}

		return { risk, riskStatus };
	},
	prepareSignalsCounterpartiesAml: arr => {
		const arrs = {
			red: [],
			orange: [],
			green: [],
			violet: [],
			blue: [],
		};

		arr?.forEach(({ Type, PercentageAmount = 0, TotalAmount, RiskScore, Counterparties }) => {
			const { color, title } = TypesService.riskSignalsParam[Type] || {};

			if (!arrs[color] || !color || !title) return console.error('Lost meaning for:', { Type, color, title });

			arrs[color].push({
				color,
				title,
				riskScore: RiskScore,
				amount: TotalAmount,
				counterparties: Counterparties,
				percent: PercentageAmount,
			});
		});

		const resultArr = Object.values(arrs)
			.map(el => el.sort((a, b) => b.riskScore - a.riskScore))
			.flat();

		return resultArr;
	},
	getBlockchainNameByCurrencySymbol(symbol) {
		const blockchainNames = {
			BTC: 'Bitcoin',
			TRX: 'Tron',
			'USDT-TRC20': 'Tron',
			ETH: 'Ethereum',
			LTC: 'Litecoin',
			'USDT-ERC20': 'Ethereum',
			'USDC-ERC20': 'Ethereum',
			BNB: 'BSC',
			'USDT-BEP20': 'BSC',
			'USDC-BEP20': 'BSC',
		};

		return blockchainNames[symbol] || symbol;
	},
	GetCurrencyPrecisionByName: (name = 'BTC', defaultPrecision = 2) => {
		const precisions = {
			BTC: 8,
			'USDT-TRC20': 2,
			TRX: 6,
			ETH: 6,
			BNB: 6,
			LTC: 8,
			'USDT-ERC20': 2,
			'USDC-ERC20': 2,
			'USDT-BEP20': 2,
			'USDC-BEP20': 2,
			[CREDIT_CURRENCY]: 2,
		};
		return precisions[name] || defaultPrecision;
	},
	decryptHash: async (encryptedBase64, base64Key = 'FBwWMtNzo30cKiNiOCwvY+liJvj8OJsZb331nt405DQ=') => {
		try {
			const encryptedData = CryptoJS.enc.Base64.parse(encryptedBase64);
			const key = CryptoJS.enc.Base64.parse(base64Key);

			const iv = CryptoJS.enc.Base64.parse('AAAAAAAAAAAAAAAAAAAA==');

			const decrypted = CryptoJS.AES.decrypt({ ciphertext: encryptedData }, key, {
				iv: iv,
				mode: CryptoJS.mode.CBC,
				padding: CryptoJS.pad.Pkcs7,
			});

			return decrypted.toString(CryptoJS.enc.Utf8);
		} catch (err) {
			console.error('Ошибка расшифровки:', err);
			return 'Невозможно расшифровать!';
		}
	},
	showNotification: ({
		id,
		type,
		icon,
		iconSize,
		firstBtnText,
		onClickFirstBtn,
		iconLeftFirstBtn,
		iconRightFirstBtn,
		secondBtnText,
		onClickSecondBtn,
		iconLeftSecondBtn,
		iconRightSecondBtn,
		title,
		description,
		visibleClose,
		positions,
		persist,
		autoHideDuration = 5000,
		dontShowAgainValue,
		className,
		onClose = () => {},
	}) => {
		if (dontShowAgainValue !== undefined && !id)
			console.warn('Для работы функционала "Больше не показывать" должно быть задано id.'); // Функционал пока нерабочий. При необходимости нужно будет доработать.

		const key = id || `snackbar-${String(Math.random()).substring(2, 10)}`;
		const defaultPositions = { vertical: 'top', horizontal: 'center' };
		const anchorOrigin = positions ? { ...defaultPositions, ...positions } : defaultPositions;

		return enqueueSnackbar('', {
			content: Confirmation({
				id: key,
				type,
				icon,
				iconSize,
				firstBtnText,
				onClickFirstBtn,
				iconLeftFirstBtn,
				iconRightFirstBtn,
				secondBtnText,
				onClickSecondBtn,
				iconLeftSecondBtn,
				iconRightSecondBtn,
				title,
				description,
				visibleClose,
				close: id => {
					closeSnackbar(id);
					onClose(id);
				},
				autoHideDuration,
				className,
			}),
			anchorOrigin,
			persist,
			autoHideDuration,
			key,
		});
	},
	checkCorrectnesAddress: (currency, address) => {
		switch (currency) {
			case 'BTC':
				return /^(tb1|bc1|[13])[a-zA-HJ-NP-Z0-9]{25,39}$/.test(address);
			case 'USDT-TRC20':
			case 'TRX':
				return /T[A-Za-z1-9]{33}/.test(address) && address?.length === 34;
			case 'USDT-ERC20':
			case 'ETH':
				return /^0x[a-fA-F0-9]{40}$/.test(address);
			default:
				return true;
		}
	},
	debounce(func, timeout = 500) {
		// вызывает функцию только если с прошлого вызова прошло время timeout
		let timer;
		return (...args) => {
			clearTimeout(timer);
			timer = setTimeout(() => {
				func.apply(this, args);
			}, timeout);
		};
	},
	showAllIcons() {
		return (
			<div
				style={{
					display: 'flex',
					maxWidth: '1200px',
					flexWrap: 'wrap',
					borderLeft: '1px solid grey',
					borderTop: '1px solid grey',
				}}
			>
				{Object.keys(Icons).map((iconName, index) => {
					const blackBackground = ['CANCELED', 'AML_SCAN', 'CREDIT_CARD', 'VISA'];
					return (
						<div
							style={{
								padding: '10px',
								borderRight: '1px solid grey',
								borderBottom: '1px solid grey',
								background: blackBackground.includes(iconName) ? 'black' : 'white',
							}}
						>
							<Icon
								type={Icons[iconName]}
								width={40}
								height={40}
								onClick={() => navigator.clipboard.writeText(iconName)}
							/>
						</div>
					);
				})}
			</div>
		);
	},
	formatDate: (date, splitSymbol = '.', splitDate = 10, postFix = '', revert = false, toUTC = false) => {
		if (!date || typeof date !== 'object') return date;
		const yyyy = toUTC ? date.getUTCFullYear() : date.getFullYear();
		let dd = toUTC ? date.getUTCDate() : date.getDate();
		let mm = toUTC ? date.getUTCMonth() + 1 : date.getMonth() + 1;

		if (dd < splitDate) {
			dd = '0' + dd;
		}

		if (mm < splitDate) {
			mm = '0' + mm;
		}

		if (revert) return yyyy + splitSymbol + mm + splitSymbol + dd + postFix;

		return dd + splitSymbol + mm + splitSymbol + yyyy + postFix;
	},
	confirmPin: ({
		requestMethod = () => {},
		requestData = {},
		resolveFunc = null,
		rejectFunt = null,
		header = null,
		subheader = null,
		status = null,
		onFillPin = null,
		pinUpdateHandler = () => {},
	}) => {
		const confirmHandler =
			onFillPin ||
			(async pin => {
				const defaultResolveFunc = () => {
					router.navigate(-1);
					store.dispatch(appSlice.actions.setPinConfirm());
					HelperService.showNotification({ type: 'success', title: t('Success') });
				};
				const defaultRejectFunt = error => {
					router.navigate(-1);
					store.dispatch(appSlice.actions.setPinConfirm());
					RequestService.BaseErrorFunc(error);
				};

				const resolveHandler = resolveFunc || defaultResolveFunc;
				const rejectHandler = rejectFunt || defaultRejectFunt;

				try {
					store.dispatch(appSlice.actions.setPinConfirmProps({ loading: true }));
					const InitData = store.getState().app.initDataTG;

					const response = await requestMethod({ ...requestData, PinCode: pin, InitData });

					store.dispatch(appSlice.actions.setPinConfirmProps({ loading: false }));

					if (response?.Status === 'Error') {
						if (response?.ErrorCode === 'InvalidPinCodeError') {
							HelperService.showNotification({ type: 'error', title: t('Pin-code is incorrect. Try again') });
							store.dispatch(appSlice.actions.setPinConfirmProps({ status: 'error' }));
						} else if (response?.ErrorCode === 'WrongPinCodeLimitReachedError') {
							HelperService.showNotification({ type: 'error', title: t('Incorrect PIN! Login temporarily blocked') });
						} else {
							resolveHandler(response);
						}
					} else {
						resolveHandler(response);
					}
				} catch (error) {
					rejectHandler(error);
				}
			});

		store.dispatch(
			appSlice.actions.setPinConfirm({
				header,
				subheader,
				status,
				pinUpdateHandler,
				confirmHandler,
			})
		);
		router.navigate(routes.enterPin);
	},
	verifyEmailPhone: ({
		verificationEmail = false,
		verificationPhone = false,
		successNotifficationText = 'Success',
		successRedirectRoute = routes.mainPage,
		emailCodeResendAvailableTime = 60,
		phoneCodeResendAvailableTime = 60,
	}) => {
		router.navigate(
			{ pathname: routes.verificationEmailPhone },
			{
				state: {
					verificationEmail,
					verificationPhone,
					successNotifficationText,
					successRedirectRoute,
					emailCodeResendAvailableTime,
					phoneCodeResendAvailableTime,
				},
			}
		);
	},
	middleEllipsis: (string = '', startLength = 8, endLength = null) => {
		const endLengthValue = endLength || startLength;
		if (string.length < startLength + endLengthValue) return string;
		return string.slice(0, startLength) + '...' + string.slice(-endLengthValue);
	},
	formatRelativeTime: date => {
		console.log('Date from back2 ', date);
		const activeLanguage = langFormat[store.getState().localization.lang];
		const now = new Date();
		const diffSec = Math.floor((now - date) / 1000);
		const diffMin = Math.floor(diffSec / 60);
		const diffHours = Math.floor(diffMin / 60);

		console.log('DiffSec:', diffSec);
		console.log('DiffMin', diffMin);
		console.log('DiffHours: ', diffHours);

		if (diffSec < 60) return t('Online');

		if (diffMin < 60)
			return new Intl.RelativeTimeFormat(activeLanguage, { numeric: 'auto' }).format(-diffMin, 'minute');

		return new Intl.RelativeTimeFormat(activeLanguage, { numeric: 'auto' }).format(-diffHours, 'hour');
	},
	formatLastOnline: dateInput => {
		// console.log('DateInput: ', dateInput);
		if (!dateInput) return '';

		const date = new Date(dateInput);
		if (isNaN(date)) return '';

		const now = new Date();
		const diffMs = now - date;
		const diffHours = diffMs / (1000 * 60 * 60);

		if (diffHours < 12) {
			return HelperService.formatRelativeTime(date);
		}

		const base = HelperService.formatDate(date, '.', 10, '', false);
		return base;
	},
	urlBase64ToUint8Array: base64String => {
		const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
		const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

		const rawData = window.atob(base64);
		const outputArray = new Uint8Array(rawData.length);

		for (let i = 0; i < rawData.length; ++i) {
			outputArray[i] = rawData.charCodeAt(i);
		}

		return outputArray;
	},
	detectPWALanguage: () => {
		const supported = Object.values(LOCALES);

		const browserLangs = navigator.languages?.length ? navigator.languages : [navigator.language];
		for (const lang of browserLangs) {
			const normalized = lang.toLowerCase().split('-')[0];
			if (supported.includes(normalized)) {
				return normalized;
			}
		}

		return LOCALES.en;
	},
	formatCompactNumber: (
		value,
		{ currency = '$', separator = '.', thousandSep = ',', compactFrom = 1000, negativeSign = '-' } = {}
	) => {
		const num = Number(value);

		if (!Number.isFinite(num)) return `${currency}-`; // fallback

		const sign = num < 0 ? negativeSign : '';
		const abs = Math.abs(num);
		const units = [
			{ threshold: 1, suffix: '', decimals: 2 },
			{ threshold: 1e3, suffix: 'K', decimals: 1 },
			{ threshold: 1e6, suffix: 'M', decimals: 2 },
			{ threshold: 1e9, suffix: 'B', decimals: 2 },
			{ threshold: 1e12, suffix: 'T', decimals: 2 },
			{ threshold: 1e15, suffix: 'Q', decimals: 2 },
		];

		// если после округления число >= 1000 то делаем компактный вариант
		const roundedInt = Math.round(abs);
		const useCompact = num >= compactFrom;

		// обычный формат (меньше 1000)
		if (!useCompact) {
			const fixed = abs.toFixed(2);
			const [intPart, decimalPart] = fixed.split('.');
			const withSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);
			return `${sign}${currency}${withSep}${separator}${decimalPart}`;
		}

		let unit = units[0];
		for (let i = units.length - 1; i >= 0; i--) {
			if (roundedInt >= units[i].threshold) {
				unit = units[i];
				break;
			}
		}

		const factor = Math.pow(10, unit.decimals);
		let valueInUnit = roundedInt / unit.threshold;

		// округление
		valueInUnit = Math.round(valueInUnit * factor) / factor;

		// если округление перепрыгнуло порог (999.9K -> 1.0M)
		if (valueInUnit >= 1000 && unit.suffix !== 'Q') {
			const nextIndex = units.findIndex(u => u.threshold === unit.threshold) + 1;
			unit = units[nextIndex];
			valueInUnit = 1;
		}

		let str = valueInUnit.toFixed(unit.decimals);
		if (unit.suffix !== 'K') {
			str = str.replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '1$');
		}

		if (separator !== '.') {
			str = str.replace('.', separator);
		}

		return `${sign}${currency}${str}${unit.suffix}`;
	},
};

export default HelperService;
